import { getBodyElement } from "@gr-exports/async/dom";
import { onInit } from "@gr-exports/async/tracking";
import { withSpan } from "@gr-exports/async/apm";
import { loggerScope } from "../parser/utils.js";
import {
  getLockInitTokenFromElement,
  getTrackingAttributes,
  getTrackingEventsFromElement,
} from "../parser/attributes.js";
import { ELEMENT_SELECTOR, getTrackableElements } from "../parser/elements.js";
import { synteticEvents } from "../event/event.js";
import { initializeAbortSignal, trackingEventListener } from "../event/listener.js";
import { registerPageImpression } from "../syntetic/pageImpression.js";
import { registerVisibilityTracking, unregisterVisibilityTracking } from "../syntetic/visible.js";
import { lockInit, withInitLock } from "./defer.js";

const log = loggerScope.scope("init");

export const tracked = new WeakMap<
  HTMLElement,
  [listener: typeof trackingEventListener, string[]]
>();

/**
 *
 *
 *
 *
 */
export function registerTrackingHandler(
  elementToTrack: HTMLElement,
  attrs: ReturnType<typeof getTrackingAttributes>,
): void {
  /*                                                                         */
  /*                              */
  if (tracked.has(elementToTrack)) return;

  log.trace("Register Data tracker for", elementToTrack);
  const eventsToTrack = getTrackingEventsFromElement(elementToTrack, attrs);

  /*                                                          */
  const lockInitToken = getLockInitTokenFromElement(elementToTrack, attrs);
  if (lockInitToken) lockInit(lockInitToken);

  const controller = new AbortController();
  const listener = trackingEventListener.bind([elementToTrack, controller]);

  eventsToTrack.forEach((eventName) => {
    elementToTrack.addEventListener(eventName, listener, { passive: true });
  });

  /*                                                  */
  initializeAbortSignal(elementToTrack, listener, controller.signal);

  /*                                                                 */
  tracked.set(elementToTrack, [listener, eventsToTrack]);

  /*                                                     */
  if (eventsToTrack.includes(synteticEvents[0])) {
    registerVisibilityTracking(elementToTrack);
  }

  /*                                                          */
  if (eventsToTrack.includes(synteticEvents[1])) {
    registerPageImpression(elementToTrack);
  }
}

/**
 *
 *
 *
 *
 */
export function unregisterTrackingHandler(elementToTrack: HTMLElement): void {
  const cacheData = tracked.get(elementToTrack);
  if (!cacheData) {
    return;
  }

  /*                                                                          */
  cacheData[1].forEach((eventName) => {
    elementToTrack.removeEventListener(eventName, cacheData[0]);
  });

  /*                                                               */
  unregisterVisibilityTracking(elementToTrack);

  /*                                   */
  tracked.delete(elementToTrack);
}

/*                                                                    */
export const observer = new MutationObserver((mutations) => {
  mutations
    .filter((m) => m.type === "childList")
    .forEach((mutation) => {
      const trackableElements = getTrackableElements(mutation.addedNodes);
      trackableElements.forEach((e) => withInitLock(e, registerTrackingHandler));

      const removedElements = getTrackableElements(mutation.removedNodes);
      removedElements.forEach(unregisterTrackingHandler);
    });
});

/**
 *
 *
 *
 *
 *
 */
export const initDataTracker = withSpan(
  async function (this): Promise<void> {
    log.info("Initialize tracking system");
    const theBody = getBodyElement();

    /*                                            */
    /*                                                  */
    /*                          */
    await Promise.race([
      new Promise<void>((r) => setTimeout(r, 1000)),
      new Promise<void>((r) => onInit.sub(r, { once: true })),
    ]);

    log.info("Bootstrap tracking system");
    /*                                                           */
    /*                                                             */
    const elementsToTrack = theBody.querySelectorAll<HTMLElement>(ELEMENT_SELECTOR);
    elementsToTrack.forEach((e) => withInitLock(e, registerTrackingHandler));

    /*                                            */
    this?.addLabels({ gr_tracking_element_count: elementsToTrack.length });

    /*                                     */
    observer.observe(theBody, { childList: true, subtree: true });
  },
  "Init Tracking Wrapper",
  "module-init.global-resources.tracking",
);
